import axios, { isAxiosError } from "axios";
import apiClient, { ApiMessage } from "./client";
import { calcTimeRange, type TimeFilter } from "./dashboard";

export { calcTimeRange, type TimeFilter };

/** 活动明细条目 */
export interface ActivityLogItem {
  id: string;
  actionTime: number;
  actionType: "VIEW" | "DOWNLOAD";
  userId: string;
  agentName: string;
  agentEmail?: string;
  agentCompany?: string;
  agentRegion?: string;
  materialId: string;
  materialTitle: string;
  materialType?: string;
  primaryCategory?: string;
  category?: string;
  modelCategory?: string;
}

/** 活动明细查询参数 */
export interface ActivityLogQuery {
  startTime: number;
  endTime: number;
  userId?: string;
  materialId?: string;
  actionType?: "VIEW" | "DOWNLOAD" | "";
  keyword?: string;
  pageNumber?: number;
  pageSize?: number;
}

/** 日期字符串转当日 0 点毫秒（本地时区） */
export function dateToStartMs(dateStr: string): number {
  return new Date(dateStr + "T00:00:00").getTime();
}

/** 日期字符串转次日 0 点毫秒，用于 [start, end) 右开区间 */
export function dateToEndMsExclusive(dateStr: string): number {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return d.getTime();
}

/** 校验 [startTime, endTime) 区间，不通过返回错误文案 */
export function validateTimeRange(startTime: number, endTime: number): string | null {
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
    return "时间参数无效";
  }
  if (startTime >= endTime) {
    return "开始时间不能大于或等于结束时间";
  }
  return null;
}

/**
 * 从日期输入或快捷预设计算 [startTime, endTime)
 * - 同时有 start/end：左闭右开自然日区间
 * - 仅 start：单日
 * - 仅 end：结束日当天
 * - 均无：走 preset
 */
export function resolveTimeRange(
  startDate: string,
  endDate: string,
  timePreset: TimeFilter
): [number, number] {
  if (startDate && endDate) {
    return [dateToStartMs(startDate), dateToEndMsExclusive(endDate)];
  }
  if (startDate) {
    return [dateToStartMs(startDate), dateToEndMsExclusive(startDate)];
  }
  if (endDate) {
    return [dateToStartMs(endDate), dateToEndMsExclusive(endDate)];
  }
  return calcTimeRange(timePreset);
}

function appendQueryParams(formData: URLSearchParams, query: ActivityLogQuery) {
  formData.append("startTime", String(query.startTime));
  formData.append("endTime", String(query.endTime));
  if (query.userId) formData.append("userId", query.userId);
  if (query.materialId) formData.append("materialId", query.materialId);
  if (query.actionType) formData.append("actionType", query.actionType);
  if (query.keyword?.trim()) formData.append("keyword", query.keyword.trim());
}

/** 从 blob / text 响应中解析后端 JSON 错误信息 */
async function parseExportErrorPayload(data: unknown): Promise<string | null> {
  if (data instanceof Blob) {
    const text = await data.text();
    if (!text) return null;
    try {
      const json = JSON.parse(text) as ApiMessage;
      return json.info || "导出失败";
    } catch {
      return text;
    }
  }
  if (typeof data === "string" && data) {
    try {
      const json = JSON.parse(data) as ApiMessage;
      return json.info || "导出失败";
    } catch {
      return data;
    }
  }
  return null;
}

/**
 * 活动明细分页列表
 * POST /agentstat/listActivityLogs.do
 */
export async function listActivityLogs(
  query: ActivityLogQuery
): Promise<{ list: ActivityLogItem[]; total: number; pageNumber: number; pageSize: number }> {
  const rangeErr = validateTimeRange(query.startTime, query.endTime);
  if (rangeErr) throw new Error(rangeErr);

  const formData = new URLSearchParams();
  appendQueryParams(formData, query);
  formData.append("pageNumber", String(query.pageNumber ?? 1));
  formData.append("pageSize", String(query.pageSize ?? 20));

  const res = await apiClient.post("/agentstat/listActivityLogs.do", formData.toString());
  const d = res.data as ApiMessage & {
    data?: { list: ActivityLogItem[]; total: number; pageNumber: number; pageSize: number };
  };
  if (d && d.success && d.data) {
    return d.data;
  }
  throw new Error(d?.info || "获取活动明细失败");
}

/**
 * 导出活动明细 Excel（blob）
 * POST /agentstat/exportActivityLogs.do
 */
export async function exportActivityLogs(
  query: Omit<ActivityLogQuery, "pageNumber" | "pageSize">
): Promise<{ blob: Blob; filename: string }> {
  const rangeErr = validateTimeRange(query.startTime, query.endTime);
  if (rangeErr) throw new Error(rangeErr);

  const formData = new URLSearchParams();
  appendQueryParams(formData, query);

  const token = localStorage.getItem("agent_token");

  try {
    const res = await axios.post("/api/agentstat/exportActivityLogs.do", formData.toString(), {
      responseType: "blob",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        ...(token ? { token } : {}),
      },
      timeout: 120000,
    });

    const contentType = String(res.headers["content-type"] || "");
    if (contentType.includes("application/json") || contentType.includes("text/plain")) {
      const msg = await parseExportErrorPayload(res.data);
      throw new Error(msg || "导出失败");
    }

    let filename = "activity_logs.xls";
    const disposition = res.headers["content-disposition"] as string | undefined;
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match?.[1]) {
        filename = decodeURIComponent(match[1].replace(/['"]/g, ""));
      }
    }

    return { blob: res.data as Blob, filename };
  } catch (err) {
    if (isAxiosError(err) && err.response?.data) {
      const msg = await parseExportErrorPayload(err.response.data);
      if (msg) throw new Error(msg);
    }
    if (err instanceof Error) throw err;
    throw new Error("导出失败");
  }
}
